import React from "react";
import { Briefcase, FileText, Users, Wrench, Armchair, House, Key, CalendarClock } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function ManagementSolutions() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  const ownerServices = [{
    title: "Financial Reporting",
    description: "Comprehensive monthly statements and transparent accounting for your peace of mind.",
    icon: <FileText className="size-6 text-primary" />
  }, {
    title: "Tenant Placement",
    description: "Rigorous screening processes to ensure reliable occupancy and care for your property.",
    icon: <Users className="size-6 text-primary" />
  }, {
    title: "Maintenance Coordination",
    description: "24/7 response to repair requests, preserving your asset's value and tenant satisfaction.",
    icon: <Wrench className="size-6 text-primary" />
  }, {
    title: "Market Analysis",
    description: "Data-driven insights to maximize your rental income based on local Abilene trends.",
    icon: <Briefcase className="size-6 text-primary" />
  }];
  const renterOptions = [{
    title: "Short-Term Furnished",
    description: "Flexible, move-in ready spaces perfect for corporate stays, travel nurses, or temporary relocation needs.",
    icon: <Armchair className="size-8 text-primary mb-4" />,
    features: ["Fully Furnished", "Utilities Included", "Flexible Terms"]
  }, {
    title: "Long-Term Residential",
    description: "Stable, high-quality unfurnished homes for individuals and families looking to plant roots in Abilene.",
    icon: <House className="size-8 text-primary mb-4" />,
    features: ["Pet Friendly Options", "Online Portal", "Prompt Maintenance"]
  }];
  return <section id="management-solutions" ref={ref} className={`dark bg-neutral-900 text-white py-24 overflow-hidden transition-opacity duration-700 ${isVisible ? "opacity-100" : "opacity-0"}`}>
      <div className="container mx-auto px-4 md:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24">
          
          <div className="flex flex-col gap-8">
            <div className="space-y-4">
              <h2 className="text-3xl md:text-4xl font-serif font-bold tracking-tight text-white">
                For Property Owners
              </h2>
              <p className="text-neutral-300 text-lg max-w-md">
                Maximize your investment with our comprehensive management solutions. 
                We handle the details so you can enjoy the rewards.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {ownerServices.map((service, i) => <Card key={i} data-index={i} className="text-card-foreground flex flex-col gap-6 rounded-none py-6 border-white/10 transition-colors duration-300 !bg-[hsl(210,9%,31%)] hover:bg-neutral-700 hover:border-secondary/50">
                  <CardHeader className="pb-2">
                    <div className="mb-2 !text-[hsl(210,16%,93%)]">{service.icon}</div>
                    <CardTitle className="font-semibold text-lg text-white">{service.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-sm text-neutral-300">
                      {service.description}
                    </CardDescription>
                  </CardContent>
                </Card>)}
            </div>

            <div className="pt-4">
              <Button asChild size="lg" className="w-full sm:w-auto bg-secondary hover:bg-secondary/90 text-white border-none">
                <Link to="/OwnerServicesPage">
                  Explore Owner Services
                </Link>
              </Button>
            </div>
          </div>

          
          <div className="flex flex-col gap-8 lg:pt-12">
            <div className="space-y-4">
              <h2 className="text-3xl md:text-4xl font-serif font-bold tracking-tight text-white">
                For Residents
              </h2>
              <p className="text-neutral-300 text-lg max-w-md">
                Find your perfect home in Abilene. Whether you need a temporary stay or a long-term residence, we have quality options for you.
              </p>
            </div>

            <div className="space-y-6">
              {renterOptions.map((option, i) => <div key={i} data-index={i} className="group relative overflow-hidden rounded-none border border-white/10 p-8 transition-all duration-300 !bg-[hsl(210,9%,31%)] hover:bg-neutral-800">
                  <div className="absolute top-0 right-0 p-6 opacity-10 group-hover:opacity-20 transition-opacity">
                    {option.icon}
                  </div>
                  
                  <div className="relative z-10">
                    <div className="flex items-center gap-3 mb-4">
                      {option.icon}
                      <h3 className="text-2xl font-semibold text-white">{option.title}</h3>
                    </div>
                    <p className="text-neutral-300 mb-6 text-lg">
                      {option.description}
                    </p>
                    <ul className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      {option.features.map((feature, j) => <li key={j} className="flex items-center gap-2 text-sm font-medium text-white">
                          <span className="block size-1.5 rounded-full bg-secondary" />
                          {feature}
                        </li>)}
                    </ul>
                  </div>
                </div>)}
            </div>

            <div className="pt-4">
              <Button asChild variant="outline" size="lg" className="w-full sm:w-auto border-white text-white hover:bg-white hover:text-black">
                
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}